import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";

const AREA_LABELS = {
  INI: "Iniziativa", AFF: "Affidabilita", MET: "Metodo", QUA: "Qualita", FOC: "Focus",
  ANA: "Analisi", CRE: "Creativita", REL: "Relazione", INF: "Influenza", INT: "Integrita"
};
const AREAS = ["INI","AFF","MET","QUA","FOC","ANA","CRE","REL","INF","INT"];
const COLORS = ["#1B4FD8","#56A3F5","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6","#F97316","#6366F1"];

// B73 — Fascia dai numeri (fallback) o dai campi già band-view (backend serializzato)
function getBandColor(score) {
  if (score >= 90) return "#0071e3";
  if (score >= 75) return "#34c759";
  if (score >= 60) return "#ffcc00";
  if (score >= 40) return "#ff9500";
  return "#ff3b30";
}

function getBandLabel(score) {
  if (score >= 90) return "Dominante";
  if (score >= 75) return "Solida";
  if (score >= 60) return "Promettente";
  if (score >= 40) return "Potenziale";
  return "Critica";
}

function bandMidpoint(score) {
  if (score >= 90) return 95;
  if (score >= 75) return 82;
  if (score >= 60) return 67;
  if (score >= 40) return 50;
  return 20;
}

// B73 — legge l'entry di uno slot area/indice, gestendo sia la nuova struttura
// band-mode {label,color,band_midpoint} sia la vecchia {score:N} (admin+raw).
function readSlot(entry) {
  if (!entry) return { label: "N/A", color: "#999", band_midpoint: 0 };
  // Nuova struttura: già serializzata
  if (typeof entry.band_midpoint === "number" || entry.label) {
    return {
      label: entry.label || "N/A",
      color: entry.color || "#999",
      band_midpoint: typeof entry.band_midpoint === "number" ? entry.band_midpoint : bandMidpoint(entry.score || 0),
      score: typeof entry.score === "number" ? entry.score : undefined,
    };
  }
  // Vecchia struttura numerica
  const s = typeof entry.score === "number" ? entry.score : (typeof entry === "number" ? entry : 0);
  return { label: getBandLabel(s), color: getBandColor(s), band_midpoint: bandMidpoint(s), score: s };
}

/** Single candidate/employee radar — B73: quantizzato al midpoint fascia (5 step). */
export function ScoreRadar({ scores, name = "", size = 320 }) {
  if (!scores?.areas) return null;
  const data = AREAS.map(a => {
    const slot = readSlot(scores.areas[a]);
    return {
      area: AREA_LABELS[a] || a,
      short: a,
      value: slot.band_midpoint,     // B73: vertice al midpoint, non al valore esatto
      label: slot.label,             // usato dal tooltip
      color: slot.color,
      fullMark: 100,
    };
  });
  return (
    <div data-testid="score-radar">
      <ResponsiveContainer width="100%" height={size}>
        <RadarChart data={data} cx="50%" cy="50%" outerRadius="72%">
          <PolarGrid stroke="rgba(13,31,60,0.08)" />
          <PolarAngleAxis dataKey="short" tick={{ fontSize: 11, fill: "#0D1F3C", fontFamily: "Poppins" }} />
          {/* B73: rimossi tick numerici sull'asse radiale */}
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} tickCount={6} />
          <Radar name={name || "Profilo"} dataKey="value" stroke="#1B4FD8" fill="#1B4FD8" fillOpacity={0.15} strokeWidth={2} dot={{ r: 3, fill: "#1B4FD8" }} />
          <Tooltip formatter={(_v, _n, ctx) => [ctx?.payload?.label || "", ""]} contentStyle={{ fontFamily: "Poppins", fontSize: 12, borderRadius: 8, border: "1px solid rgba(13,31,60,0.1)" }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Compare multiple candidates/employees on one radar — quantizzato. */
export function CompareRadar({ profiles, size = 380 }) {
  if (!profiles || profiles.length === 0) return null;
  const data = AREAS.map(a => {
    const point = { area: AREA_LABELS[a] || a, short: a, fullMark: 100 };
    profiles.forEach((p, i) => {
      const slot = readSlot(p.scores?.areas?.[a]);
      point[`p${i}`] = slot.band_midpoint;
      point[`p${i}_label`] = slot.label;
    });
    return point;
  });
  return (
    <div data-testid="compare-radar">
      <ResponsiveContainer width="100%" height={size}>
        <RadarChart data={data} cx="50%" cy="50%" outerRadius="72%">
          <PolarGrid stroke="rgba(13,31,60,0.08)" />
          <PolarAngleAxis dataKey="short" tick={{ fontSize: 11, fill: "#0D1F3C", fontFamily: "Poppins" }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} tickCount={6} />
          {profiles.map((p, i) => (
            <Radar key={i} name={p.name} dataKey={`p${i}`} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.08} strokeWidth={2} dot={{ r: 3, fill: COLORS[i % COLORS.length] }} />
          ))}
          <Legend wrapperStyle={{ fontFamily: "Poppins", fontSize: 12 }} />
          <Tooltip formatter={(_v, _n, ctx) => {
            const key = ctx?.dataKey;
            const label = key ? ctx?.payload?.[`${key}_label`] : "";
            return [label || "", ""];
          }} contentStyle={{ fontFamily: "Poppins", fontSize: 12, borderRadius: 8, border: "1px solid rgba(13,31,60,0.1)" }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Score bars — B73: 5 step discreti, no percentuali, colorate per fascia, label testuale. */
export function ScoreBars({ scores }) {
  if (!scores?.areas) return null;
  return (
    <div className="space-y-2" data-testid="score-bars">
      {AREAS.map(a => {
        const slot = readSlot(scores.areas[a]);
        return (
          <div key={a} className="flex items-center gap-3">
            <span className="text-xs font-medium text-[#0D1F3C]/60 w-8">{a}</span>
            <div className="flex-1 h-5 bg-[#0D1F3C]/5 rounded-full overflow-hidden relative">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${slot.band_midpoint}%`, backgroundColor: slot.color }} />
            </div>
            <span className="text-xs font-medium w-24 text-right" style={{ color: slot.color }}>{slot.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Indices badges — B73: label + color (senza numero, senza %). */
export function IndicesBadges({ scores, testType = "candidate" }) {
  if (!scores) return null;
  const items = [
    { key: "transparency", label: testType === "management" ? "ITM" : "Trasparenza" },
    { key: "stability", label: testType === "management" ? "Stabilita Man." : "Stabilita" },
    { key: "coherence", label: testType === "management" ? "Commitment" : testType === "employee" ? "Motivazione" : "Coerenza" }
  ];
  return (
    <div className="flex gap-2 flex-wrap" data-testid="indices-badges">
      {items.map(({ key, label }) => {
        const slot = readSlot(scores[key]);
        return (
          <div key={key} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border" style={{ borderColor: `${slot.color}40`, backgroundColor: `${slot.color}08` }}>
            <span className="text-xs text-[#0D1F3C]/60">{label}:</span>
            <span className="text-xs font-bold" style={{ color: slot.color }}>{slot.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Ranking — B73: ordina per band_midpoint aggregato (backend `avg_view.band_midpoint`),
 *  mostra solo label media (Dominante/Solida/…), niente numeri.
 */
export function CandidateRanking({ candidates, onSelect }) {
  const withView = candidates.filter(c => c.scores);
  const bandOf = (c) => {
    // Preferisce avg_view dal backend; fallback: media dei band_midpoint locali
    if (c.avg_view?.band_midpoint) return c.avg_view;
    const areas = c.scores?.areas || {};
    const mids = Object.values(areas).map(v => readSlot(v).band_midpoint);
    const m = mids.length ? mids.reduce((a, b) => a + b, 0) / mids.length : 0;
    return { label: getBandLabel(m), color: getBandColor(m), band_midpoint: bandMidpoint(m) };
  };
  const sorted = [...withView].sort((a, b) => bandOf(b).band_midpoint - bandOf(a).band_midpoint);
  if (sorted.length === 0) return <p className="text-sm text-[#0D1F3C]/40 text-center py-4">Nessun candidato con analisi completata</p>;
  return (
    <div className="space-y-2" data-testid="candidate-ranking">
      {sorted.map((c, idx) => {
        const b = bandOf(c);
        return (
          <div key={c.id} onClick={() => onSelect?.(c)}
            className="flex items-center gap-3 p-3 rounded-xl border border-[#0D1F3C]/8 hover:border-[#1B4FD8]/30 hover:shadow-sm transition-all cursor-pointer"
            data-testid={`ranking-item-${idx}`}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${b.color}15`, color: b.color }}>
              {idx + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0D1F3C] truncate">{c.first_name} {c.last_name}</p>
              <p className="text-xs text-[#0D1F3C]/40">Profilo</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold" style={{ color: b.color }}>{b.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { AREA_LABELS, AREAS, COLORS, getBandColor, getBandLabel, bandMidpoint, readSlot };
